import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useUserSettings } from "@/contexts"
import type { UnitName } from "@/models/unit"
import { translateUnit } from "@/utils/unit"

/**
 * Hook to get the appropriate label string for a unit in the active units system.
 * A value is sometimes needed to determine the appropriate label string by getting the unit.
 *
 * @example
 * const co2eUnit = useUnit(UnitName.MetricTonsOfCarbonDioxideEquivalent)
 *
 * @example
 * const co2eUnit = useUnit(UnitName.MetricTonsOfCarbonDioxideEquivalent, 2000)
 */
export const useUnit = (
  unit?: UnitName | null,
  value?: number | string | null
): string => {
  const { i18n } = useTranslation()
  const { currency, unitsSystem } = useUserSettings()

  const unitDisplay = useMemo(() => {
    return translateUnit({
      currencyCode: currency,
      localeCode: i18n.language,
      unit,
      unitsSystem,
      value,
    })
  }, [currency, i18n.language, unit, unitsSystem, value])

  return unitDisplay
}
